import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchLesson } from "@/lib/api";
import type { LessonData } from "@/types/lesson";
import type { LessonAttachment, LessonResponse } from "@/types/api";
import { LessonPlayerScreen } from "@/components/LessonPlayerScreen";

function parseLessonContent(raw: LessonData | string): LessonData | null {
  if (!raw) return null;
  if (typeof raw === "object" && raw.lesson_title && Array.isArray(raw.slides)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw) as LessonData;
      return parsed?.slides ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

export function LessonView() {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>();
  const [lessonData, setLessonData] = useState<LessonData | null>(null);
  const [lessonMeta, setLessonMeta] = useState<{ lessonId: number; courseId: number; eal_level?: number } | null>(null);
  const [attachments, setAttachments] = useState<LessonAttachment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cid = courseId ? parseInt(courseId, 10) : NaN;
    const lid = lessonId ? parseInt(lessonId, 10) : NaN;
    if (!cid || !lid || Number.isNaN(cid) || Number.isNaN(lid)) {
      setError("Invalid course or lesson ID");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    fetchLesson(cid, lid)
      .then((res: LessonResponse) => {
        const parsed = parseLessonContent(res.lesson_content);
        if (!parsed) {
          setError("Lesson content is not interactive_slides format");
          return;
        }
        setLessonData(parsed);
        setLessonMeta({
          lessonId: res.id,
          courseId: res.course_id,
          eal_level: res.eal_level,
        });
        // All attachments (including videos) go to the completion screen.
        // LessonAttachments renders video files as players, not download links.
        setAttachments(Array.isArray(res.attachments) ? res.attachments : []);
      })
      .catch((e) => setError(e.message || "Failed to load lesson"))
      .finally(() => setLoading(false));
  }, [courseId, lessonId]);

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center p-8">
        <p className="text-slate-600">Loading lesson…</p>
      </div>
    );
  }

  if (error || !lessonData || !lessonMeta) {
    return (
      <div className="min-h-full flex items-center justify-center p-8">
        <p className="text-red-600">{error || "Lesson not found"}</p>
      </div>
    );
  }

  return (
    <LessonPlayerScreen
      lesson={lessonData}
      lessonId={lessonMeta.lessonId}
      courseId={lessonMeta.courseId}
      ealLevel={lessonMeta.eal_level}
      attachments={attachments}
    />
  );
}
